import KpiAxisConfigurationBinCountField from 'components/molecules/PackArchitecture/KpiAxisConfigurationBinCountField';
import KpiAxisConfigurationBreakpointsField from 'components/molecules/PackArchitecture/KpiAxisConfigurationBreakpointsField';
import KpiAxisConfigurationBinsField from 'components/molecules/PackArchitecture/KpiAxisConfigurationBinsField';
import KpiRangeSlider from 'components/molecules/PackArchitecture/KpiRangeSlider';
import type {
    KpiAxisConfiguration,
    KpiBinLabelAffixes,
} from 'components/molecules/PackArchitecture/kpiAxisConfiguration.utils';
import {
    buildKpiPreview,
    sanitizeKpiBreakpoints,
    type KpiDataRange,
} from 'components/molecules/PackArchitecture/kpiAxisConfiguration.utils';
import type { KpiAxisControlsMeta } from 'components/molecules/PackArchitecture/kpiAxisControlsMeta.types';
import { useKpiRangeInput } from 'components/molecules/PackArchitecture/useKpiRangeInput';

const SectionDivider = () => <div className="border-t border-backgroundLightGrey" />;

type KpiAxisConfigurationModalConfigurationControlsProps = {
    config: KpiAxisConfiguration;
    controlsMeta: KpiAxisControlsMeta;
    dataRange: KpiDataRange;
    defaultBinCount: number;
    binLabelAffixes?: KpiBinLabelAffixes;
    onChange: (patch: Partial<KpiAxisConfiguration>) => void;
};

const KpiAxisConfigurationModalConfigurationControls = ({
    config,
    controlsMeta,
    dataRange,
    defaultBinCount,
    binLabelAffixes,
    onChange,
}: KpiAxisConfigurationModalConfigurationControlsProps) => {
    const preview = buildKpiPreview(config, binLabelAffixes);
    const isCustomBinning = config.binningMethod === 'custom';
    const sliderBreakpoints = isCustomBinning ? config.breakpoints : preview.breakpoints;
    const { minInputError, maxInputError, applyMinValue, applyMaxValue, updateRangeFromSlider } = useKpiRangeInput({
        dataRange,
        config,
        onChange,
    });

    const handleBinningMethodChange = (binningMethod: KpiAxisConfiguration['binningMethod']) => {
        if (binningMethod === 'uniform') {
            onChange({
                binningMethod: 'uniform',
                binCount: config.binCount || defaultBinCount,
            });
            return;
        }

        onChange({ binningMethod: 'custom' });
    };

    const handleBreakpointsChange = (breakpoints: number[]) => {
        onChange({
            breakpoints: sanitizeKpiBreakpoints(breakpoints, config.min, config.max, dataRange),
        });
    };

    const handleRevertToUniform = () => {
        onChange({
            binningMethod: 'uniform',
            binCount: defaultBinCount,
            breakpoints: [],
        });
    };

    return (
        <div className="flex h-full w-[476px] shrink-0 flex-col gap-6 border-r border-backgroundLightGrey bg-white p-6">
            <div className="flex flex-col gap-4">
                <p className="m-0 text-base font-medium leading-6 text-blackTitle">{controlsMeta.range.label}</p>
                <KpiRangeSlider
                    dataRange={dataRange}
                    min={config.min}
                    max={config.max}
                    binningMethod={config.binningMethod}
                    breakpoints={sliderBreakpoints}
                    minInputError={minInputError}
                    maxInputError={maxInputError}
                    minInputLabel={controlsMeta.range.minInput.label}
                    maxInputLabel={controlsMeta.range.maxInput.label}
                    onRangeChange={updateRangeFromSlider}
                    onMinInput={applyMinValue}
                    onMaxInput={applyMaxValue}
                    onBreakpointsChange={isCustomBinning ? handleBreakpointsChange : undefined}
                />
            </div>

            <SectionDivider />

            <div className="flex flex-col gap-6">
                <KpiAxisConfigurationBinsField
                    controlsMeta={controlsMeta.bins}
                    value={config.binningMethod}
                    onChange={handleBinningMethodChange}
                />
                {isCustomBinning ? (
                    <KpiAxisConfigurationBreakpointsField
                        min={config.min}
                        max={config.max}
                        breakpoints={config.breakpoints}
                        dataRange={dataRange}
                        breakPointLabel={controlsMeta.bins.custom.breakPoint.label}
                        onBreakpointsChange={handleBreakpointsChange}
                        onRevertToUniform={handleRevertToUniform}
                    />
                ) : (
                    <KpiAxisConfigurationBinCountField
                        min={config.min}
                        max={config.max}
                        binCount={config.binCount}
                        binSize={preview.binSize}
                        countLabel={controlsMeta.bins.uniform.count.label}
                        sizeLabel={controlsMeta.bins.uniform.size.label}
                        onBinCountChange={(binCount) => onChange({ binCount })}
                    />
                )}
            </div>
        </div>
    );
};

export default KpiAxisConfigurationModalConfigurationControls;
